import styled from 'styled-components';
import {
  Modal,
  ModalBody,
  ModalTitle,
  ModalFooter,
  ModalFooterAlignment,
  MODAL_INNER_GAP,
} from 'design-system/src/components/Modal';
import { Typography } from 'design-system/src/components/Typography/Typography';
import Button from 'design-system/src/components/Button/Button';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Hairline from 'design-system/src/components/Hairline/Hairline';
import {
  Drug,
  NewCommentFile,
  Patient,
  client,
  FormFilesDocumentTypes,
  OutboundTaskType,
  OutboundTaskTypeKey,
} from '../../api';
import { ExtraInfoTable } from 'design-system/src/components/ExtraInfoTable/ExtraInfoTable';
import CommentAndFileSection from './CommentAndFileSection';
import { useNavigate } from 'react-router-dom';
import { useState, useRef } from 'react';
import toast from 'react-hot-toast';
import { StandardErrorToast, Toast } from 'design-system/src/components/Toast/Toast';
import * as Sentry from '@sentry/react';
import { tasksQueryKey } from '../../queries';

const StyledCommentAndFileSection = styled(CommentAndFileSection)`
  padding-bottom: 0;
`;

// Map task types to their instruction text
const TASK_INSTRUCTIONS: Partial<Record<OutboundTaskTypeKey, string>> = {
  [OutboundTaskTypeKey.PA_DENIAL_NEXT_STEPS]:
    'If you would like to do something other than appeal or cancel this prescription, please explain below.',
  [OutboundTaskTypeKey.PA_DENIAL_NEXT_STEPS_AND_APPEAL_REVIEW]:
    'If you would like to do something other than appeal or cancel this prescription, please explain below.',
  [OutboundTaskTypeKey.APPEAL_REVIEW]:
    'If you would like to do something other than appeal or cancel this prescription, please explain below.',
  [OutboundTaskTypeKey.NEW_RX_DOCUMENT_UPLOAD]:
    'If you would like to take an action that is not supported by this document upload task, please upload any relevant files or add an explanation below.',
  [OutboundTaskTypeKey.OFFERED_BEST_CASH_PRICE]:
    'If you would like to take a different action regarding this cash price offer, please explain below.',
};

const DEFAULT_INSTRUCTIONS =
  'If you would like to take a different action regarding this prescription, please explain below.';

type NewFollowupTaskModalProps = {
  closeModal: () => void;
  patient: Patient;
  drug: Drug;
  patientId: string;
  prescriptionId: string;
  taskId: string;
  /** Stable enum key for task type - use for any logic gating */
  relatedTaskKey: OutboundTaskTypeKey;
  /** Human-readable task type label - used for API calls to backend */
  relatedTaskType: OutboundTaskType;
};

const NewFollowupTaskModal = ({
  closeModal,
  patient,
  drug,
  patientId,
  prescriptionId,
  taskId,
  relatedTaskKey,
  relatedTaskType,
}: NewFollowupTaskModalProps) => {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [inputValue, setInputValue] = useState('');
  const [files, setFiles] = useState<NewCommentFile[]>([]);
  const queryClient = useQueryClient();

  const isTrainingMode = import.meta.env.VITE_TRAINING_MODE === 'true';
  const instructionsText = TASK_INSTRUCTIONS[relatedTaskKey] ?? DEFAULT_INSTRUCTIONS;

  const extraInfoData = [
    { label: 'Patient', value: `${patient.first_name} ${patient.last_name}` },
    { label: 'Drug', value: drug.name },
  ];

  const declineTaskAndCreateNew = useMutation({
    mutationFn: async () => {
      if (isTrainingMode) {
        console.warn(`Training Mode: Mocking "decline task and create new" for task ID: ${taskId}`);
        return;
      }
      await client.post(`tasks/${taskId}/decline`, {
        decline_reason: 'help_request',
      });
    },
    onSuccess: () => {
      if (isTrainingMode) {
        toast.custom(() => <Toast variant="success">Task closed and new task created.</Toast>);
        closeModal();
        navigate('/tasks');
        return;
      }
      createHelpRequestTask.mutate();
    },
    onError: (error) => {
      toast.custom(() => <StandardErrorToast />);
      console.error(error);
      Sentry.captureException(error);
    },
  });

  const createHelpRequestTask = useMutation<{ task_id: string }, Error, void>({
    mutationFn: async () => {
      if (isTrainingMode) {
        console.warn(`Training Mode: Mocking "create help request task" for task ID: ${taskId}`);
        return { task_id: `mock-${Date.now()}` };
      }
      // TODO: share this logic with NewTaskModal.tsx or migrate this modal to use that component
      const formData = new FormData();
      formData.append('patient_id', patientId);
      formData.append('prescription_id', prescriptionId);
      formData.append('request_text', inputValue);
      formData.append('task_source', 'portal_task_view');
      formData.append('related_task_type', relatedTaskType);

      if (files.length) {
        const formFilesDocumentTypes: FormFilesDocumentTypes = {};

        files.forEach((fileInfo) => {
          formData.append('files[]', fileInfo.file);
          formFilesDocumentTypes[fileInfo.file.name] = fileInfo.document_type;
        });

        formData.append('form_files_document_types', JSON.stringify(formFilesDocumentTypes));
      }

      return client.post(`tasks/help-request/${taskId}`, formData).then((res) => res.data);
    },
    onSuccess: ({ task_id }) => {
      queryClient.invalidateQueries({
        queryKey: tasksQueryKey(),
      });
      toast.custom(() => <Toast variant="success">Task closed and new task created.</Toast>);
      closeModal();
      navigate(`/tasks/${task_id}`);
    },
    onError: (error) => {
      toast.custom(() => <StandardErrorToast />);
      Sentry.captureException(error);
      console.error(error);
    },
  });

  const handleFilesChange = (newFiles: NewCommentFile[]) => {
    setFiles(newFiles);
  };

  return (
    <Modal onClose={closeModal}>
      <ModalTitle>Something else?</ModalTitle>

      <ModalBody>
        <ExtraInfoTable
          data={extraInfoData}
          minFieldLabelWidth="80px"
          marginTop="0"
          tableMarginBottom={MODAL_INNER_GAP}
          hairlineComponent={<Hairline $marginTop="0" $marginBottom="0" />}
        />

        <Typography>{instructionsText}</Typography>
        <Typography>
          <strong>Note:</strong> Clicking <strong>Submit</strong> will close this task and create a new task.
        </Typography>
        <StyledCommentAndFileSection
          inputRef={inputRef}
          placeholder="Add details"
          hideSubmitButton
          files={files}
          onFilesChange={handleFilesChange}
          inputValue={inputValue}
          setInputValue={setInputValue}
        />
      </ModalBody>

      <ModalFooter align={ModalFooterAlignment.SpaceBetween}>
        <Button variant="tertiary" onClick={closeModal}>
          Back
        </Button>
        <Button
          onClick={() => declineTaskAndCreateNew.mutate()}
          isLoading={declineTaskAndCreateNew.isPending || createHelpRequestTask.isPending}
        >
          Submit
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default NewFollowupTaskModal;
